import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionGateProps {
  children: React.ReactNode;
}

const SubscriptionGate = ({ children }: SubscriptionGateProps) => {
  const { subscribed, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  // Public paths that don't require authentication
  const publicPaths = ["/", "/auth", "/subscription", "/terms", "/privacy", "/forgot-password", "/install"];
  const isPublicPath = publicPaths.includes(location.pathname);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthenticated(!!session);
      
      // Redirect to auth if not authenticated and trying to access protected route
      if (!session && !isPublicPath) {
        navigate("/auth");
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthenticated(!!session);
      
      // Redirect to auth if logged out and on protected route
      if (!session && !isPublicPath) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [location.pathname, isPublicPath, navigate]);

  // Allow access to public paths immediately (no auth/subscription check needed)
  if (isPublicPath) {
    return <>{children}</>;
  }

  // Show loading while checking authentication or subscription for protected routes
  if (authenticated === null || subscriptionLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </main>
    );
  }

  // Block access if not authenticated (shouldn't reach here due to redirect, but safety check)
  if (!authenticated) {
    return null;
  }

  // Block access if not subscribed
  if (!subscribed) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Subscription Required</CardTitle>
            <CardDescription>
              You need an active BacSense Pro subscription to access this feature
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <p className="font-semibold">BacSense Pro - €199/month</p>
              <p className="text-sm text-muted-foreground">
                ✓ 15-day free trial<br />
                ✓ AI-powered recommendations<br />
                ✓ Advanced analytics<br />
                ✓ Full inventory management
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate("/subscription")}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              View Subscription Options
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return <>{children}</>;
};

export default SubscriptionGate;
